import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import "./ChartCard.scss";
import dayjs from "dayjs";
import PropTypes from "prop-types";
import Skeleton from "../skeletonLoader/SkeletonLoader";
import EmptyState from "../emptyState/EmptyState";
import { useTranslation } from "react-i18next";

const DATE_FORMAT_CHART = "DD/MM HH:mm";

function ChartCard({
  data,
  width,
  firstLineColor,
  secondLineColor,
  isLoading,
  yAxisUnit,
  name1,
  name2,
  allowDecimals = true,
}) {
  const { t } = useTranslation("dashboard");

  return (
    <div className="chart-card">
      {isLoading ? (
        <Skeleton className="chart-card-skeleton" />
      ) : (
        <>
          {data.length === 0 ? (
            <div className="chart-card-no-data">
              <EmptyState title={t("no_data")} text={t("nothing_to_display_right_now")} />
            </div>
          ) : (
            <div className="chart-card-graph">
              <div className="chart-card-graph-scroll" style={width ? { width } : undefined}>
                <ResponsiveContainer width="99%" height="100%">
                  <LineChart
                    data={[...data].sort(
                      (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
                    )}
                  >
                    <CartesianGrid vertical={false} stroke="#2C3454" />
                    <XAxis
                      dataKey="date"
                      stroke="#8A94A6"
                      type="category"
                      textAnchor="end"
                      angle={-30}
                      height={80}
                      dy={10}
                      tickFormatter={(date) => dayjs(date).format(DATE_FORMAT_CHART)}
                    />
                    <YAxis
                      stroke="#8A94A6"
                      domain={[0, "auto"]}
                      width={100}
                      tickFormatter={(v) => `${v} ${yAxisUnit}`}
                      allowDecimals={allowDecimals}
                    />
                    <Tooltip
                      formatter={(value) => `${value} ${yAxisUnit}`}
                      labelFormatter={(label) => dayjs(label).format(DATE_FORMAT_CHART)}
                      contentStyle={{
                        backgroundColor: "#1e1e2f",
                        borderRadius: 6,
                        color: "#fff",
                        padding: "8px 12px",
                        fontSize: 12,
                      }}
                      itemStyle={{ color: "#fff" }}
                      labelStyle={{ fontWeight: "bold", marginBottom: 4 }}
                    />
                    <Line
                      type="monotone"
                      dataKey="value1"
                      stroke={firstLineColor}
                      strokeWidth={2}
                      name={name1}
                    />
                    <Line
                      type="monotone"
                      dataKey="value2"
                      stroke={secondLineColor}
                      strokeWidth={2}
                      name={name2}
                    />
                    {name1 && name2 && (
                      <Legend
                        formatter={(value) => <span style={{ color: "white" }}>{value}</span>}
                      />
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

ChartCard.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string.isRequired,
      value1: PropTypes.number,
      value2: PropTypes.number,
    }),
  ).isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  firstLineColor: PropTypes.string,
  secondLineColor: PropTypes.string,
  isLoading: PropTypes.bool,
  yAxisUnit: PropTypes.string,
  name1: PropTypes.string,
  name2: PropTypes.string,
};

export default ChartCard;
